import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import {
  getBatchSyncStatus,
  syncBatchSubjectClass,
  syncBatchSubjectTutor,
  syncBatchStudent,
  updateBatchStudentEmail,
} from '../../redux/features/batchSlice';

const ENTITY_LABELS = {
  class: { label: 'Class', idLabel: 'Wise Class ID', idPlaceholder: 'Paste the class ID from Lens' },
  tutor: {
    label: 'Tutor',
    idLabel: 'Wise Teacher ID or Email',
    idPlaceholder: 'Paste the teacher ID, or the email used to create the Lens account',
  },
  student: {
    label: 'Student',
    idLabel: 'Wise Student ID or Email',
    idPlaceholder: 'Paste the student ID, or the email used to create the Lens account',
  },
};

const THUNK_BY_TYPE = {
  class: syncBatchSubjectClass,
  tutor: syncBatchSubjectTutor,
  student: syncBatchStudent,
};

const StatusIcon = ({ value }) =>
  value ? (
    <CheckCircleIcon fontSize="small" sx={{ color: 'success.main', verticalAlign: 'middle' }} titleAccess="Synced" />
  ) : (
    <CancelIcon fontSize="small" sx={{ color: 'error.main', verticalAlign: 'middle' }} titleAccess="Not synced" />
  );

// A quiet dash reads as "not applicable" — reserve the red X for things that
// should have happened and haven't, not for a tutor-mapped check that has
// nothing to check yet because no tutor is assigned at all.
const NotApplicable = () => (
  <Typography component="span" sx={{ color: 'text.disabled' }}>
    —
  </Typography>
);

const SummaryChip = ({ label, synced, total }) => (
  <Chip
    size="small"
    variant="outlined"
    label={`${label}: ${synced}/${total}`}
    color={total === 0 ? 'default' : synced === total ? 'success' : 'warning'}
  />
);

// A single clickable "Not synced" chip that opens this. Whether a class,
// tutor, or student already exists in Lens, or needs to be created there for
// the first time, isn't something the portal can tell on its own — there's no
// local record either way once we know it's unmapped. So this asks: staff
// either point at an existing Lens entity (link) or ask us to create one — we
// never guess. Shared across all three columns; only the labels differ.
const SyncEntityPopover = ({ open, anchorEl, onClose, onSync, syncing, entityLabel, idLabel, idPlaceholder }) => {
  const [mode, setMode] = useState('create');
  const [entityId, setEntityId] = useState('');
  const [confirmedNoDuplicate, setConfirmedNoDuplicate] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode('create');
      setEntityId('');
      setConfirmedNoDuplicate(false);
    }
  }, [open]);

  // Switching away from "create" doesn't need the confirmation; switching
  // back to it should ask again rather than remember a stale tick.
  useEffect(() => {
    setConfirmedNoDuplicate(false);
  }, [mode]);

  const canSubmit = mode === 'create' ? confirmedNoDuplicate : entityId.trim().length > 0;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={syncing ? undefined : onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Stack spacing={1.5} sx={{ p: 2, width: 300 }}>
        <Box>
          <Typography variant="subtitle2">Sync {entityLabel} with Lens</Typography>
          <Typography variant="caption" color="text.secondary">
            Does this {entityLabel.toLowerCase()} already exist in Lens, or does it need to be created?
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={mode}
          disabled={syncing}
          onChange={(e, value) => value && setMode(value)}
        >
          <ToggleButton value="create">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <AddCircleOutlineIcon fontSize="small" />
              <span>Create new</span>
            </Stack>
          </ToggleButton>
          <ToggleButton value="link">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <LinkIcon fontSize="small" />
              <span>Already exists</span>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === 'link' && (
          <TextField
            size="small"
            label={idLabel}
            placeholder={idPlaceholder}
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            disabled={syncing}
            autoFocus
            fullWidth
          />
        )}

        {mode === 'create' && (
          <FormControlLabel
            sx={{ alignItems: 'flex-start', ml: 0 }}
            control={
              <Checkbox
                size="small"
                checked={confirmedNoDuplicate}
                disabled={syncing}
                onChange={(e) => setConfirmedNoDuplicate(e.target.checked)}
                sx={{ pt: 0 }}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                I've checked Lens and this {entityLabel.toLowerCase()} genuinely doesn't exist there yet.
              </Typography>
            }
          />
        )}

        <LoadingButton
          fullWidth
          variant="contained"
          size="small"
          loading={syncing}
          disabled={!canSubmit}
          onClick={() => onSync(mode, entityId.trim())}
        >
          {mode === 'create' ? 'Create & Sync' : `Link ${entityLabel}`}
        </LoadingButton>
      </Stack>
    </Popover>
  );
};

// A green check for "has a Lens account" doesn't mean "assigned to THIS
// course" — assignment is fire-and-forget with no persisted result, so we
// can't tell locally whether it's needed. Always-available rather than
// conditional: re-running the assignment for someone already on the roster is
// a no-op in Lens, not an error.
const ReassignAction = ({ loading, onClick, tooltip }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
    <StatusIcon value />
    <Tooltip title={tooltip}>
      <span>
        <IconButton size="small" disabled={loading} onClick={onClick}>
          {loading ? <CircularProgress size={14} /> : <SyncIcon fontSize="inherit" />}
        </IconButton>
      </span>
    </Tooltip>
  </Stack>
);

// Lens has no bulk endpoint, so "Sync selected" walks the ticked students one
// by one through the same "Already exists" link call staff use by hand,
// passing the student's email (editable in the table, saved to the student). Each row reports how its own attempt went; anything
// that fails (no Lens account with that email, etc.) is left for a manual
// sync from its Not synced chip.
const BULK_STATUS = {
  queued: { label: 'Queued', color: 'default' },
  syncing: { label: 'Syncing', color: 'info' },
  synced: { label: 'Synced', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  skipped: { label: 'No email', color: 'warning' },
};

// A failed row gets an arrow that opens the reason underneath it, so staff
// can read why without hovering each chip.
const BulkStatusChip = ({ status, message, expanded, onToggle }) => {
  if (!status) return <NotApplicable />;
  const { label, color } = BULK_STATUS[status];
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
      <Tooltip title={status === 'failed' ? '' : message || ''}>
        <Chip
          size="small"
          variant="outlined"
          color={color}
          label={label}
          icon={status === 'syncing' ? <CircularProgress size={12} /> : undefined}
        />
      </Tooltip>
      {status === 'failed' && (
        <Tooltip title={expanded ? 'Hide error' : 'Show error'}>
          <IconButton size="small" onClick={onToggle}>
            {expanded ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

const BatchSyncStatus = ({ rowData, open, setOpen }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, syncStatus } = useSelector((state) => state.batch);
  const { subjects = [], students = [] } = syncStatus || {};

  // { anchorEl, type: 'class' | 'tutor' | 'student', targetId }
  const [syncPopover, setSyncPopover] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // { type, targetId } of whichever reassign action is currently in flight
  const [reassigning, setReassigning] = useState(null);
  // { [course_enrollment_id]: { status, message } } for the current bulk run
  const [bulkStatus, setBulkStatus] = useState({});
  const [bulkSyncing, setBulkSyncing] = useState(false);
  // course_enrollment_ids whose failure message is opened under the row
  const [expandedErrors, setExpandedErrors] = useState([]);
  // course_enrollment_ids ticked for the bulk sync
  const [selected, setSelected] = useState([]);
  // { [course_enrollment_id]: email } typed but not yet saved to the student
  const [emailEdits, setEmailEdits] = useState({});
  // course_enrollment_id whose email save is in flight
  const [savingEmail, setSavingEmail] = useState(null);
  // Stops the bulk loop if the dialog is closed mid-run
  const bulkStopped = useRef(false);

  useEffect(() => {
    if (open && rowData?.course_batch_id) {
      dispatch(getBatchSyncStatus(rowData.course_batch_id));
    }
  }, [open]);

  useEffect(() => {
    bulkStopped.current = false;
    return () => {
      bulkStopped.current = true;
    };
  }, []);

  const subjectsSynced = subjects.filter((s) => s.wise_synced).length;
  const assignedSubjects = subjects.filter((s) => s.tutor_assigned);
  const tutorsSynced = assignedSubjects.filter((s) => s.tutor_wise_synced).length;
  const studentsSynced = students.filter((s) => s.wise_synced).length;
  const unsyncedStudents = students.filter((s) => !s.wise_synced);
  const hasBulkRun = Object.keys(bulkStatus).length > 0;
  const selectedStudents = unsyncedStudents.filter((s) => selected.includes(s.course_enrollment_id));
  const emailFor = (student) => emailEdits[student.course_enrollment_id] ?? student.email ?? '';

  const toggleSelected = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleExpandedError = (id) =>
    setExpandedErrors((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelectAll = (checked) =>
    setSelected(checked ? unsyncedStudents.map((s) => s.course_enrollment_id) : []);

  const refresh = () => dispatch(getBatchSyncStatus(rowData.course_batch_id));

  const idFieldFor = (type) => (type === 'student' ? 'course_enrollment_id' : 'subject_enrollment_id');
  const entityIdFieldFor = (type) =>
    type === 'class' ? 'wise_class_id' : type === 'tutor' ? 'wise_teacher_id' : 'wise_student_id';

  const handleSync = (mode, entityId) => {
    if (!syncPopover || !rowData?.course_batch_id) return;
    setSyncing(true);

    const { type, targetId } = syncPopover;
    const payload = {
      batchID: rowData.course_batch_id,
      mode,
      [idFieldFor(type)]: targetId,
      [entityIdFieldFor(type)]: entityId,
    };

    dispatch(THUNK_BY_TYPE[type](payload)).then((res) => {
      setSyncing(false);
      if (res.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message, { variant: 'success' });
        setSyncPopover(null);
        refresh();
      } else {
        enqueueSnackbar(res.payload?.message || 'Failed to sync with Lens', { variant: 'error' });
      }
    });
  };

  const isEmailEdited = (student) => {
    const edit = emailEdits[student.course_enrollment_id];
    return edit !== undefined && edit.trim() !== (student.email || '');
  };

  const discardEmailEdit = (id) => setEmailEdits(({ [id]: _, ...rest }) => rest);

  const handleSaveEmail = (student) => {
    const id = student.course_enrollment_id;
    const email = emailEdits[id]?.trim();
    if (!isEmailEdited(student)) {
      discardEmailEdit(id);
      return Promise.resolve(true);
    }

    setSavingEmail(id);
    return dispatch(updateBatchStudentEmail({ batchID: rowData.course_batch_id, course_enrollment_id: id, email })).then(
      (res) => {
        setSavingEmail(null);
        if (res.payload?.status === 'success') {
          discardEmailEdit(id);
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          return true;
        }
        enqueueSnackbar(res.payload?.message || 'Failed to update email', { variant: 'error' });
        return false;
      }
    );
  };

  const handleBulkSync = async () => {
    if (!rowData?.course_batch_id || selectedStudents.length === 0) return;
    setBulkSyncing(true);
    setExpandedErrors([]);
    setBulkStatus(Object.fromEntries(selectedStudents.map((s) => [s.course_enrollment_id, { status: 'queued' }])));

    const setRowStatus = (id, status, message) => setBulkStatus((prev) => ({ ...prev, [id]: { status, message } }));

    let synced = 0;
    for (const student of selectedStudents) {
      if (bulkStopped.current) return;
      const id = student.course_enrollment_id;
      const email = emailFor(student).trim();

      if (!email) {
        setRowStatus(id, 'skipped', 'No email on record — sync this student manually');
        continue;
      }

      // An email typed but not saved yet is saved first, so the student
      // record matches the account it gets linked to
      // eslint-disable-next-line no-await-in-loop
      if (isEmailEdited(student) && !(await handleSaveEmail(student))) {
        setRowStatus(id, 'failed', "Couldn't save the edited email");
        continue;
      }

      setRowStatus(id, 'syncing');
      // Sequential on purpose: Lens is a third-party API with no bulk option,
      // so we don't fire every request at once.
      // eslint-disable-next-line no-await-in-loop
      const res = await dispatch(
        syncBatchStudent({
          batchID: rowData.course_batch_id,
          mode: 'link',
          course_enrollment_id: id,
          wise_student_id: email,
        })
      );
      if (res.payload?.status === 'success') {
        synced += 1;
        setRowStatus(id, 'synced', res.payload.message);
      } else {
        setRowStatus(id, 'failed', res.payload?.message || 'Failed to sync with Lens');
      }
    }

    if (bulkStopped.current) return;
    setBulkSyncing(false);
    setSelected([]);
    enqueueSnackbar(`Bulk sync finished — ${synced}/${selectedStudents.length} students synced`, {
      variant: synced === selectedStudents.length ? 'success' : 'warning',
    });
    if (synced > 0) refresh();
  };

  const handleReassign = (type, targetId) => {
    if (!rowData?.course_batch_id) return;
    setReassigning({ type, targetId });
    dispatch(
      THUNK_BY_TYPE[type]({
        batchID: rowData.course_batch_id,
        mode: 'reassign',
        [idFieldFor(type)]: targetId,
      })
    ).then((res) => {
      setReassigning(null);
      if (res.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message, { variant: 'success' });
      } else {
        enqueueSnackbar(res.payload?.message || 'Failed to re-assign', { variant: 'error' });
      }
    });
  };

  return (
    <GlobalDialog
      title={rowData?.course_batch_name ? `Lens Sync Status — ${rowData.course_batch_name}` : 'Lens Sync Status'}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={'md'}
      closeButton={true}
      content={
        <Stack spacing={2}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && subjects.length === 0 && (
            <Alert severity="info" variant="outlined">
              No subjects found for this batch
            </Alert>
          )}

          {!loading && subjects.length > 0 && (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <SummaryChip label="Subjects Synced" synced={subjectsSynced} total={subjects.length} />
                <SummaryChip label="Tutors Wise-Mapped" synced={tutorsSynced} total={assignedSubjects.length} />
                <SummaryChip label="Students Synced" synced={studentsSynced} total={students.length} />
              </Stack>

              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                      <TableCell>Subject</TableCell>
                      <TableCell align="center">Wise Class</TableCell>
                      <TableCell>Tutor</TableCell>
                      <TableCell align="center">Tutor Wise-Mapped</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subjects.map((subject) => {
                      const fullySynced = subject.wise_synced && subject.tutor_assigned && subject.tutor_wise_synced;
                      return (
                        <TableRow key={subject.subject_enrollment_id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: fullySynced ? 'success.main' : 'warning.main',
                                  flexShrink: 0,
                                }}
                                titleAccess={fullySynced ? 'Fully synced' : 'Needs attention'}
                              />
                              <Typography variant="body2">{subject.subject_name || 'Untitled subject'}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            {subject.wise_synced ? (
                              <StatusIcon value />
                            ) : (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="error"
                                clickable
                                icon={<CancelIcon fontSize="small" />}
                                label="Not synced"
                                title="Click to sync this class with Lens"
                                onClick={(e) =>
                                  setSyncPopover({
                                    anchorEl: e.currentTarget,
                                    type: 'class',
                                    targetId: subject.subject_enrollment_id,
                                  })
                                }
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {subject.tutor_assigned ? (
                              <Typography variant="body2">{subject.tutor_name?.trim() || 'Unnamed tutor'}</Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                Not assigned
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {!subject.tutor_assigned ? (
                              <NotApplicable />
                            ) : subject.tutor_wise_synced ? (
                              <ReassignAction
                                loading={reassigning?.type === 'tutor' && reassigning.targetId === subject.subject_enrollment_id}
                                onClick={() => handleReassign('tutor', subject.subject_enrollment_id)}
                                tooltip="Has a Lens account — re-assign them to this course in case that never went through"
                              />
                            ) : (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="error"
                                clickable
                                icon={<CancelIcon fontSize="small" />}
                                label="Not synced"
                                title={subject.wise_synced ? 'Click to sync this tutor with Lens' : 'Sync the class first'}
                                disabled={!subject.wise_synced}
                                onClick={(e) =>
                                  setSyncPopover({
                                    anchorEl: e.currentTarget,
                                    type: 'tutor',
                                    targetId: subject.subject_enrollment_id,
                                  })
                                }
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Students</Typography>
                  {unsyncedStudents.length > 0 && (
                    <Tooltip title="Links each selected student to their existing Lens account using their email, one at a time">
                      <span>
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          startIcon={<SyncIcon />}
                          loading={bulkSyncing}
                          loadingPosition="start"
                          disabled={selectedStudents.length === 0}
                          onClick={handleBulkSync}
                        >
                          Sync selected via email ({selectedStudents.length})
                        </LoadingButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
                {students.length === 0 ? (
                  <Alert severity="info" variant="outlined">
                    No active students in this batch
                  </Alert>
                ) : (
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              disabled={bulkSyncing || unsyncedStudents.length === 0}
                              checked={unsyncedStudents.length > 0 && selectedStudents.length === unsyncedStudents.length}
                              indeterminate={selectedStudents.length > 0 && selectedStudents.length < unsyncedStudents.length}
                              onChange={(e) => toggleSelectAll(e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>Student</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell align="center">Wise-Mapped</TableCell>
                          {hasBulkRun && <TableCell align="center">Bulk Sync</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {students.map((student) => (
                          <React.Fragment key={student.course_enrollment_id}>
                            <TableRow hover>
                              <TableCell padding="checkbox">
                                {!student.wise_synced && (
                                  <Checkbox
                                    size="small"
                                    disabled={bulkSyncing}
                                    checked={selected.includes(student.course_enrollment_id)}
                                    onChange={() => toggleSelected(student.course_enrollment_id)}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {student.student_name || 'Unnamed student'}
                                  {student.admn_no ? ` (${student.admn_no})` : ''}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {student.wise_synced ? (
                                  student.email ? (
                                    <Typography variant="body2">{student.email}</Typography>
                                  ) : (
                                    <NotApplicable />
                                  )
                                ) : (
                                  <TextField
                                    size="small"
                                    variant="standard"
                                    placeholder="Email used in Lens"
                                    value={emailFor(student)}
                                    disabled={bulkSyncing || savingEmail === student.course_enrollment_id}
                                    onChange={(e) =>
                                      setEmailEdits((prev) => ({ ...prev, [student.course_enrollment_id]: e.target.value }))
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveEmail(student);
                                      if (e.key === 'Escape') discardEmailEdit(student.course_enrollment_id);
                                    }}
                                    InputProps={{
                                      endAdornment:
                                        savingEmail === student.course_enrollment_id ? (
                                          <InputAdornment position="end">
                                            <CircularProgress size={14} />
                                          </InputAdornment>
                                        ) : isEmailEdited(student) ? (
                                          <InputAdornment position="end">
                                            <Tooltip title="Save email">
                                              <IconButton size="small" color="success" onClick={() => handleSaveEmail(student)}>
                                                <CheckIcon fontSize="inherit" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Undo">
                                              <IconButton
                                                size="small"
                                                onClick={() => discardEmailEdit(student.course_enrollment_id)}
                                              >
                                                <CloseIcon fontSize="inherit" />
                                              </IconButton>
                                            </Tooltip>
                                          </InputAdornment>
                                        ) : null,
                                    }}
                                    fullWidth
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {student.wise_synced ? (
                                  <ReassignAction
                                    loading={
                                      reassigning?.type === 'student' && reassigning.targetId === student.course_enrollment_id
                                    }
                                    onClick={() => handleReassign('student', student.course_enrollment_id)}
                                    tooltip="Has a Lens account — re-add them to this batch's classes in case that never went through"
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    clickable
                                    icon={<CancelIcon fontSize="small" />}
                                    label="Not synced"
                                    title="Click to sync this student with Lens"
                                    disabled={bulkSyncing}
                                    onClick={(e) =>
                                      setSyncPopover({
                                        anchorEl: e.currentTarget,
                                        type: 'student',
                                        targetId: student.course_enrollment_id,
                                      })
                                    }
                                  />
                                )}
                              </TableCell>
                              {hasBulkRun && (
                                <TableCell align="center">
                                  <BulkStatusChip
                                    status={bulkStatus[student.course_enrollment_id]?.status}
                                    message={bulkStatus[student.course_enrollment_id]?.message}
                                    expanded={expandedErrors.includes(student.course_enrollment_id)}
                                    onToggle={() => toggleExpandedError(student.course_enrollment_id)}
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                            {bulkStatus[student.course_enrollment_id]?.status === 'failed' && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ py: 0, borderBottom: 'none' }}>
                                  <Collapse in={expandedErrors.includes(student.course_enrollment_id)} unmountOnExit>
                                    <Alert severity="error" variant="outlined" sx={{ my: 1 }}>
                                      {bulkStatus[student.course_enrollment_id].message}
                                    </Alert>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </>
          )}

          <SyncEntityPopover
            open={Boolean(syncPopover)}
            anchorEl={syncPopover?.anchorEl}
            onClose={() => setSyncPopover(null)}
            onSync={handleSync}
            syncing={syncing}
            entityLabel={ENTITY_LABELS[syncPopover?.type]?.label || ''}
            idLabel={ENTITY_LABELS[syncPopover?.type]?.idLabel || ''}
            idPlaceholder={ENTITY_LABELS[syncPopover?.type]?.idPlaceholder || ''}
          />
        </Stack>
      }
    />
  );
};

export default BatchSyncStatus;
